import React, { useContext, useMemo } from 'react';
import type { ProjectMember, ProjectMemberRole } from '../types';
import { AuthContext } from '../contexts/AuthContext';
import { useGetCurrentProjectMemberQuery } from '../services/teamApi';

/**
 * Hook to check user permissions for various actions
 * @param projectId - Optional project ID for project-specific permission checks
 */
export const usePermissions = (projectId?: string) => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;

  // Get project member info if projectId is provided
  const { data: memberData, isLoading: isLoadingMember } = useGetCurrentProjectMemberQuery(
    { projectId: projectId! },
    { skip: !projectId || !user?.id || !authContext?.isAuthenticated }
  );

  // Extract project member from response
  const projectMember = useMemo((): ProjectMember | null => {
    if (!memberData?.success || !memberData.data) return null;
    return memberData.data;
  }, [memberData]);

  /**
   * Check if user has any of the specified permissions
   * @param requiredPermissions - Array of permission strings
   */
  const hasPermission = (requiredPermissions: string[]): boolean => {
    // If no user or not authenticated, deny access
    if (!user || !authContext?.isAuthenticated) {
      return false;
    }

    // Global admin always has access
    if (user.role === 'admin') {
      return true;
    }

    // For project-specific permissions, check project member permissions
    if (projectId && projectMember) {
      const userPermissions = projectMember.permissions || [];
      return requiredPermissions.some(permission => userPermissions.includes(permission));
    }

    // For global operations without project context, deny access (only admin would pass above check)
    return false;
  };

  /**
   * Check if user has the specified role
   * @param requiredRoles - Array of role strings
   */
  const hasRole = (requiredRoles: ProjectMemberRole[]): boolean => {
    // If no user or not authenticated, deny access
    if (!user || !authContext?.isAuthenticated) {
      return false;
    }

    if (user.role === 'admin') {
      return true;
    }

    if (projectId && projectMember) {
      return requiredRoles.includes(projectMember.role);
    }

    return false;
  };

  /**
   * Contract-specific permission checks
   * Note: Frontend permissions are permissive - real authorization happens on backend
   */
  const contractPermissions = {
    canView: () => {
      // If no user or not authenticated, deny access
      if (!user || !authContext?.isAuthenticated) {
        return false;
      }
      // For authenticated users, allow viewing contracts (backend will enforce real permissions)
      return true;
    },
    canCreate: () => {
      // If no user or not authenticated, deny access
      if (!user || !authContext?.isAuthenticated) {
        return false;
      }
      // Admin can always create
      if (user.role === 'admin') {
        return true;
      }
      // For project context, allow creation (backend will check project member permissions)
      return !!projectId;
    },
    canEdit: () => {
      // If no user or not authenticated, deny access
      if (!user || !authContext?.isAuthenticated) {
        return false;
      }
      // Admin can always edit
      if (user.role === 'admin') {
        return true;
      }
      // For project context, allow editing (backend will check project member permissions)
      return !!projectId;
    },
    canDelete: () => {
      // If no user or not authenticated, deny access
      if (!user || !authContext?.isAuthenticated) {
        return false;
      }
      // Only admin can delete (or backend will check specific permissions)
      return user.role === 'admin';
    },
    canApprove: () => {
      // If no user or not authenticated, deny access
      if (!user || !authContext?.isAuthenticated) {
        return false;
      }
      // Admin can always approve
      return user.role === 'admin';
    },
    canSign: () => {
      // If no user or not authenticated, deny access
      if (!user || !authContext?.isAuthenticated) {
        return false;
      }
      // Allow signing for authenticated users (backend will check specific permissions)
      return true;
    },
    canManageMilestones: () => {
      // If no user or not authenticated, deny access
      if (!user || !authContext?.isAuthenticated) {
        return false;
      }
      // Admin can always manage milestones
      return user.role === 'admin';
    },
    canManageParties: () => {
      // If no user or not authenticated, deny access
      if (!user || !authContext?.isAuthenticated) {
        return false;
      }
      // Admin can always manage parties
      return user.role === 'admin';
    },
    canViewFinancials: () => {
      // If no user or not authenticated, deny access
      if (!user || !authContext?.isAuthenticated) {
        return false;
      }
      // Allow viewing financials for authenticated users (backend enforces real permissions)
      return true;
    },
    canApproveFinancials: () => {
      // If no user or not authenticated, deny access
      if (!user || !authContext?.isAuthenticated) {
        return false;
      }
      // Admin can always approve financials
      return user.role === 'admin';
    },
    canComment: () => {
      // If no user or not authenticated, deny access
      if (!user || !authContext?.isAuthenticated) {
        return false;
      }
      // Allow commenting for authenticated users
      return true;
    },
    canManageCollaborators: () => {
      // If no user or not authenticated, deny access
      if (!user || !authContext?.isAuthenticated) {
        return false;
      }
      // Admin can always manage collaborators
      return user.role === 'admin';
    },
  };

  /**
   * Role-based permission checks
   */
  const rolePermissions = {
    isAdmin: () => user?.role === 'admin' && authContext?.isAuthenticated === true,
    isOwner: () => hasRole(['owner']),
    isProjectManager: () => hasRole(['project_manager']),
    isLegal: () => hasRole(['legal']),
    isFinance: () => hasRole(['finance']),
    isContractApprover: () => hasRole(['contract_approver']),
    isClient: () => hasRole(['client']),
    isContractor: () => hasRole(['contractor']),
  };

  return {
    user,
    projectMember,
    isAuthenticated: authContext?.isAuthenticated ?? false,
    isLoadingMember,
    hasPermission,
    hasRole,
    contractPermissions,
    rolePermissions,
  };
};

/**
 * Higher-order component to conditionally render content based on permissions
 */
export const PermissionGuard: React.FC<{
  permissions?: string[];
  roles?: ProjectMemberRole[];
  projectId?: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ permissions = [], roles = [], projectId, children, fallback = null }) => {
  const { hasPermission, hasRole } = usePermissions(projectId);

  const hasRequiredPermission = permissions.length === 0 || hasPermission(permissions);
  const hasRequiredRole = roles.length === 0 || hasRole(roles);

  if (hasRequiredPermission && hasRequiredRole) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};
